import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, type Profile } from '../utils/supabase';
import { useToast } from './ToastContext';

interface AuthContextType {
    user: User | null;
    profile: Profile | null;
    loading: boolean;
    error: Error | null;
    signOut: () => Promise<void>;
    signUp: (email: string, password: string) => Promise<{ success: boolean; error?: Error }>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    profile: null,
    loading: true,
    error: null,
    signOut: async () => {},
    signUp: async () => ({ success: false }),
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { showToast } = useToast();

    async function fetchProfile(userId: string) {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) throw error;
            setProfile(data);
        } catch (error) {
            setError(error instanceof Error ? error : new Error('Failed to fetch profile'));
            showToast('Error fetching profile', 'error');
        }
    }

    useEffect(() => {
        let mounted = true;

        async function initializeAuth() {
            try {
                // Check active sessions and subscribe to auth changes
                const { data: { session } } = await supabase.auth.getSession();
                
                if (mounted) {
                    setUser(session?.user ?? null);
                    if (session?.user) {
                        await fetchProfile(session.user.id);
                    }
                    // Only set loading to false after we've handled the initial session
                    setLoading(false);
                }

                // Subscribe to auth changes
                const { data: { subscription } } = supabase.auth.onAuthStateChange(
                    async (event, session) => {
                        if (mounted) {
                            setUser(session?.user ?? null);
                            if (session?.user) {
                                await fetchProfile(session.user.id);
                            }
                        }
                    }
                );

                return () => {
                    subscription.unsubscribe();
                };
            } catch (error) {
                if (mounted) {
                    setError(error instanceof Error ? error : new Error('Failed to initialize auth'));
                    setLoading(false);
                }
            }
        }

        initializeAuth();

        return () => {
            mounted = false;
        };
    }, []);

    async function signOut() {
        try {
            setLoading(true);
            const { error } = await supabase.auth.signOut();
            if (error) throw error;
        } catch (error) {
            setError(error instanceof Error ? error : new Error('Failed to sign out'));
            showToast('Error signing out', 'error');
        } finally {
            setLoading(false);
        }
    }

    async function signUp(email: string, password: string) {
        try {
            setLoading(true);
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
            });
            
            if (error) throw error;
            
            if (data.user) {
                // Create initial profile
                const { error: profileError } = await supabase
                    .from('profiles')
                    .insert([{ id: data.user.id }]);
                
                if (profileError) throw profileError;
            }
            
            return { success: true };
        } catch (error) {
            setError(error instanceof Error ? error : new Error('Failed to sign up'));
            showToast('Error creating account', 'error');
            return { success: false, error: error instanceof Error ? error : new Error('Failed to sign up') };
        } finally {
            setLoading(false);
        }
    }

    const value = {
        user,
        profile,
        loading,
        error,
        signOut,
        signUp,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
